import { API_CONFIG } from '../../config/api';
import { ApiValidationError } from '../../types/auth';
import type {
  PurchaseOrder,
  CreatePurchaseOrderData,
  UpdatePurchaseOrderData,
  SendPurchaseOrderData,
  PurchaseOrdersResponse,
  PurchaseOrderResponse,
  GetPurchaseOrdersParams,
  PurchaseOrdersResult,
} from '../../types/purchase/purchaseOrder';
import { parseContentDispositionFilename } from '../contentDisposition';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string): Promise<never> => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

const buildPurchaseOrdersQuery = (params?: GetPurchaseOrdersParams): string => {
  if (!params) return '';

  const query = new URLSearchParams();

  if (params.search) {
    query.set('search', params.search);
  }
  if (params.vendor_id != null) {
    query.set('vendor_id', String(params.vendor_id));
  }
  if (params.status) {
    query.set('status', params.status);
  }
  if (params.po_date_from) {
    query.set('po_date_from', params.po_date_from);
  }
  if (params.po_date_to) {
    query.set('po_date_to', params.po_date_to);
  }
  if (params.sort) {
    query.set('sort', params.sort);
  }
  if (params.per_page != null) {
    query.set('per_page', String(params.per_page));
  }
  if (params.page != null) {
    query.set('page', String(params.page));
  }

  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

export const getPurchaseOrders = async (
  params?: GetPurchaseOrdersParams
): Promise<PurchaseOrdersResult> => {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PURCHASE_ORDERS.GET_ALL}${buildPurchaseOrdersQuery(params)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch purchase orders');
  }

  const result: PurchaseOrdersResponse = await response.json();
  return {
    purchaseOrders: result.data ?? [],
    meta: result.meta,
  };
};

export const getPurchaseOrderById = async (id: number): Promise<PurchaseOrder> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PURCHASE_ORDERS.GET_BY_ID}/${id}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch purchase order');
  }

  const result: PurchaseOrderResponse = await response.json();
  return result.data;
};

export const createPurchaseOrder = async (
  data: CreatePurchaseOrderData
): Promise<PurchaseOrder> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PURCHASE_ORDERS.CREATE}`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create purchase order');
  }

  const result: PurchaseOrderResponse = await response.json();
  return result.data;
};

export const updatePurchaseOrder = async (
  id: number,
  data: UpdatePurchaseOrderData
): Promise<PurchaseOrder> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PURCHASE_ORDERS.UPDATE}/${id}`,
    {
      method: 'PUT',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update purchase order');
  }

  const result: PurchaseOrderResponse = await response.json();
  return result.data;
};

export const deletePurchaseOrder = async (id: number): Promise<void> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PURCHASE_ORDERS.DELETE}/${id}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete purchase order');
  }
};

export const fetchPurchaseOrderPdf = async (
  id: number,
  fallbackFilename?: string
): Promise<{ blob: Blob; filename: string }> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PURCHASE_ORDERS.PDF}/${id}/pdf`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to load purchase order PDF');
  }

  const blob = await response.blob();
  const filename =
    parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
    fallbackFilename ??
    `purchase-order-${id}.pdf`;

  return { blob, filename };
};

export const downloadPurchaseOrderPdf = async (
  id: number,
  fallbackFilename?: string
): Promise<void> => {
  const { blob, filename } = await fetchPurchaseOrderPdf(id, fallbackFilename);

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

export const sendPurchaseOrder = async (
  id: number,
  data?: SendPurchaseOrderData
): Promise<PurchaseOrder> => {
  const payload: SendPurchaseOrderData = {};
  if (data?.to?.trim()) payload.to = data.to.trim();
  if (data?.subject?.trim()) payload.subject = data.subject.trim();
  if (data?.message?.trim()) payload.message = data.message.trim();
  if (data?.cc != null) {
    if (Array.isArray(data.cc)) {
      const emails = data.cc.map((e) => e.trim()).filter(Boolean);
      if (emails.length === 1) payload.cc = emails[0];
      else if (emails.length > 1) payload.cc = emails;
    } else if (data.cc.trim()) {
      payload.cc = data.cc.trim();
    }
  }

  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PURCHASE_ORDERS.SEND}/${id}/send`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to send purchase order');
  }

  const result: PurchaseOrderResponse = await response.json();
  return result.data;
};
